<script setup lang="ts">
import FinestButton from '@/components/FinestButton.vue'
import FinestInputNumber from '@/components/FinestInputNumber.vue'
import FinestSelect from '@/components/FinestSelect.vue'
import type { EnumOption } from '@/composables/useEnumOptions'
import { idOf, matchesBlankRow, usePersistedRows } from '@/composables/usePersistedRows'
import { toNumber } from '@/utils/number'
import { requestJson } from '@/utils/postJson'
import LoanCollateralController from '@controllers/LoanCollateralController'
import { ulid } from 'ulid'
import { ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import CollateralData = App.Data.EstateObject.CollateralData
import CollateralType = App.Enums.EstateObject.Loans.CollateralType

const props = defineProps<{
  estateObjectId: string
  loanId: string | null
  ensureLoanId: () => Promise<string>
  initialCollaterals: CollateralData[]
  collateralTypeOptions: EnumOption[]
}>()

const DEFAULT_COLLATERAL_TYPE: CollateralType = 'land_charge'

interface CollateralRow {
  key: string
  id: string | null
  collateral_type: CollateralType | null
  nominal_value: number | null
}

const emit = defineEmits<{
  change: [collaterals: CollateralData[]]
}>()

const { t } = useI18n({ useScope: 'local' })

const savedLoanId = ref(props.loanId)

watch(
  () => props.loanId,
  (loanId) => {
    if (loanId) {
      savedLoanId.value = loanId
    }
  },
)

const loanIdOrFail = (): string => {
  if (!savedLoanId.value) {
    throw new Error('A collateral can only be saved for a saved loan.')
  }

  return savedLoanId.value
}

const toRow = (collateral: CollateralData): CollateralRow => ({
  key: collateral.id ?? ulid(),
  id: collateral.id ?? null,
  collateral_type: collateral.collateral_type ?? DEFAULT_COLLATERAL_TYPE,
  nominal_value: toNumber(collateral.nominal_value),
})

const blankCollateral = (): CollateralRow => ({
  key: ulid(),
  id: null,
  collateral_type: DEFAULT_COLLATERAL_TYPE,
  nominal_value: null,
})

const toPayload = (row: CollateralRow) => ({
  collateral_type: row.collateral_type,
  nominal_value: row.nominal_value,
})

const collateralRoute = (id: string) => ({ estateObject: props.estateObjectId, loan: loanIdOrFail(), collateral: id })

const { rows, reset, addDraft, removeRow, persistChanges, persistOnLeave } = usePersistedRows({
  toPayload,
  isDraft: matchesBlankRow(toPayload, blankCollateral),
  create: async (payload) =>
    idOf(
      await requestJson<CollateralData>(
        'post',
        LoanCollateralController.store({ estateObject: props.estateObjectId, loan: loanIdOrFail() }).url,
        payload,
      ),
    ),
  update: (id, payload) => requestJson('put', LoanCollateralController.update(collateralRoute(id)).url, payload),
  destroy: (id) => requestJson('delete', LoanCollateralController.destroy(collateralRoute(id)).url, undefined),
  reloadProps: ['loans', 'fieldStates', 'completion'],
})

reset(props.initialCollaterals.map(toRow))

const notifyChange = (): void =>
  emit(
    'change',
    rows.value.flatMap((row) =>
      row.id ? [{ id: row.id, collateral_type: row.collateral_type, nominal_value: row.nominal_value }] : [],
    ),
  )

const setCell = <TKey extends keyof CollateralRow>(key: string, field: TKey, value: CollateralRow[TKey]): void => {
  rows.value = rows.value.map((row) => (row.key === key ? { ...row, [field]: value } : row))

  notifyChange()
}

const addCollateral = async (): Promise<void> => {
  if (!savedLoanId.value) {
    try {
      savedLoanId.value = await props.ensureLoanId()
    } catch {
      return
    }
  }

  addDraft(blankCollateral())
}
</script>

<template>
  <section class="border-line-2 border-t pt-4" @focusout="persistOnLeave">
    <div class="mb-2 flex items-center justify-between gap-3">
      <h4 class="section-label section-label--sm">{{ t('title') }}</h4>
      <span class="text-ink-3 text-xs">{{ t('count', rows.length) }}</span>
    </div>

    <p v-if="!rows.length" class="text-ink-3 mb-2 text-sm">{{ t('empty') }}</p>

    <div v-for="row in rows" :key="row.key" class="mb-2 flex items-center gap-2">
      <FinestSelect
        class="flex-1"
        :model-value="row.collateral_type"
        :options="collateralTypeOptions"
        :aria-label="t('type')"
        @update:model-value="(value) => setCell(row.key, 'collateral_type', value as CollateralType | null)"
        @change="persistChanges" />

      <FinestInputNumber
        class="w-40"
        :model-value="row.nominal_value"
        mode="currency"
        :aria-label="t('nominalValue')"
        @update:model-value="(value) => setCell(row.key, 'nominal_value', value)" />

      <button
        type="button"
        class="text-ink-3 hover:text-danger hover:bg-danger-subtle cursor-pointer rounded-md p-2"
        :aria-label="t('remove')"
        @click="removeRow(row.key)">
        <i class="pi pi-trash text-[13px]" aria-hidden="true" />
      </button>
    </div>

    <FinestButton
      class="mt-1 border-dashed"
      variant="secondary"
      icon="pi pi-plus"
      :label="t('add')"
      @click="addCollateral" />
  </section>
</template>

<i18n lang="json">
{
  "de": {
    "title": "Sicherheiten",
    "empty": "Keine Sicherheit für diese Finanzierung hinterlegt.",
    "add": "Sicherheit hinzufügen",
    "remove": "Sicherheit entfernen",
    "type": "Art der Sicherheit",
    "nominalValue": "Nominalwert",
    "count": "{n} Sicherheit | {n} Sicherheiten"
  },
  "en": {
    "title": "Collaterals",
    "empty": "No collateral recorded for this financing.",
    "add": "Add collateral",
    "remove": "Remove collateral",
    "type": "Collateral type",
    "nominalValue": "Nominal value",
    "count": "{n} collateral | {n} collaterals"
  }
}
</i18n>
